import React, { useState, useEffect, useCallback } from "react";
import { List } from "@refinedev/antd";
import {
  Table,
  Space,
  Tag,
  Typography,
  Button,
  Input,
  Modal,
  Form,
  Select,
  DatePicker,
  message,
  Tooltip,
  Card,
  Statistic,
  Row,
  Col,
  Segmented,
} from "antd";
import {
  UserOutlined,
  CrownOutlined,
  EditOutlined,
  SearchOutlined,
  ReloadOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { supabaseClient } from "../../utility/supabaseClient";

dayjs.extend(relativeTime);

const { Text } = Typography;

// Supabase 查询返回的类型定义
interface MembershipWithTier {
  user_id: string;
  status: string;
  started_at: string | null;
  expires_at: string | null;
  created_at: string;
  membership_tiers: {
    name: string;
    display_name: string;
  } | null;  // one-to-one 关系，返回对象而非数组
}

interface UserWithSubscription {
  id: string;
  email: string;
  created_at: string;
  subscription: {
    tier: string | null;
    displayName?: string | null;
    status: string;
    started_at?: string | null;
    expires_at?: string | null;
  } | null;
}

const TIER_META: Record<string, { label: string; color: string }> = {
  free: { label: "免费会员", color: "default" },
  basic: { label: "基础会员", color: "blue" },
  premium: { label: "高级会员", color: "purple" },
  vip: { label: "VIP会员", color: "gold" },
};

const STATUS_META: Record<string, { label: string; color: string }> = {
  active: { label: "活跃", color: "green" },
  expired: { label: "已过期", color: "red" },
  inactive: { label: "未激活", color: "default" },
};

export const UserList: React.FC = () => {
  const [users, setUsers] = useState<UserWithSubscription[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchEmail, setSearchEmail] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "expired" | "inactive">("all");
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState<UserWithSubscription | null>(
    null
  );
  const [tiers, setTiers] = useState<any[]>([]);
  const [form] = Form.useForm();
  const [stats, setStats] = useState({
    totalUsers: 0,
    freeUsers: 0,
    paidUsers: 0,
    activeSubscriptions: 0,
    expiringSoon: 0,
  });

  // 加载用户列表
  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      // 获取用户总数
      const baseQuery = supabaseClient.from("user_memberships").select("id", { count: "exact", head: true });
      const countQuery =
        statusFilter === "all"
          ? baseQuery
          : baseQuery.eq("status", statusFilter === "expired" ? "expired" : statusFilter);
      const { count: totalCount, error: countError } = await countQuery;

      if (countError) {
        message.error(`统计数量失败: ${countError.message}`);
        setLoading(false);
        return;
      }

      setTotal(totalCount || 0);

      // 获取分页数据
      const from = (currentPage - 1) * pageSize;
      const to = from + pageSize - 1;

      let listQuery = supabaseClient
        .from("user_memberships")
        .select("user_id, status, started_at, expires_at, created_at, membership_tiers(name, display_name)")
        .order("created_at", { ascending: false })
        .range(from, to);

      if (statusFilter !== "all") {
        listQuery =
          statusFilter === "expired"
            ? listQuery.eq("status", "expired")
            : listQuery.eq("status", statusFilter);
      }

      const { data: memberships, error } = await listQuery as { data: MembershipWithTier[] | null; error: any };

      if (error) {
        message.error(`加载失败: ${error.message}`);
        return;
      }

      if (!memberships || memberships.length === 0) {
        setUsers([]);
        return;
      }

      // 获取用户邮箱（需要admin权限）
      // 注意：这需要使用service_role key或者admin API
      const userIds = memberships.map((m) => m.user_id);

      // 使用RPC函数获取用户信息（需要创建）
      const { data: authUsers, error: authError } = await supabaseClient.rpc(
        "get_users_by_ids",
        { user_ids: userIds }
      );

      if (authError) {
        console.error("获取用户信息失败:", authError);
        // 如果RPC函数不存在，使用备用方案
        const usersData: UserWithSubscription[] = memberships.map((m) => ({
          id: m.user_id,
          email: `用户${m.user_id.substring(0, 8)}`, // 临时显示
          created_at: m.created_at,
          subscription: {
            tier: m.membership_tiers?.name || null,
            displayName: m.membership_tiers?.display_name,
            status: m.status,
            started_at: m.started_at,
            expires_at: m.expires_at,
          },
        }));
        setUsers(usersData);
        return;
      }

      // 合并用户和订阅信息
      const usersData: UserWithSubscription[] = memberships.map((m) => {
        const authUser = authUsers?.find((u: any) => u.id === m.user_id);
        return {
          id: m.user_id,
          email: authUser?.email || "未知用户",
          created_at: authUser?.created_at || m.created_at,
          subscription: {
            tier: m.membership_tiers?.name || null,
            displayName: m.membership_tiers?.display_name,
            status: m.status,
            started_at: m.started_at,
            expires_at: m.expires_at,
          },
        };
      });

      const filteredByEmail = searchEmail
        ? usersData.filter((u) =>
            u.email.toLowerCase().includes(searchEmail.toLowerCase().trim())
          )
        : usersData;

      setUsers(filteredByEmail);
    } catch (err: any) {
      message.error(`加载异常: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, statusFilter, searchEmail]);

  // 加载统计数据
  const loadStats = useCallback(async () => {
    try {
      const { data, error } = await supabaseClient
        .from("user_memberships")
        .select("status, expires_at, membership_tiers(name)") as {
          data: Array<{
            status: string;
            expires_at: string | null;
            membership_tiers: { name: string } | null;
          }> | null;
          error: any;
        };

      if (error || !data) return;

      const stats = {
        totalUsers: data.length,
        freeUsers: data.filter((u) => {
          const tierName = u.membership_tiers?.name;
          return tierName === "free";
        }).length,
        paidUsers: data.filter((u) => {
          const tierName = u.membership_tiers?.name;
          return tierName && tierName !== "free";
        }).length,
        activeSubscriptions: data.filter((u) => u.status === "active").length,
        expiringSoon: data.filter(
          (u) =>
            u.status === "active" &&
            (u as any).expires_at &&
            dayjs((u as any).expires_at).isAfter(dayjs()) &&
            dayjs((u as any).expires_at).isBefore(dayjs().add(7, "day"))
        ).length,
      };

      setStats(stats);
    } catch (err) {
      console.error("加载统计失败:", err);
    }
  }, []);

  // 加载订阅等级
  const loadTiers = useCallback(async () => {
    try {
      const { data, error } = await supabaseClient
        .from("membership_tiers")
        .select("id, name, display_name")
        .eq("is_active", true)
        .order("display_order", { ascending: true });

      if (error) {
        message.error(`加载等级失败: ${error.message}`);
        return;
      }

      setTiers(data || []);
    } catch (err: any) {
      message.error(`加载等级异常: ${err.message}`);
    }
  }, []);

  useEffect(() => {
    loadUsers();
    loadStats();
    loadTiers();
  }, [loadUsers, loadStats, loadTiers]);

  // 打开编辑对话框
  const openEditModal = (user: UserWithSubscription) => {
    setEditingUser(user);
    form.setFieldsValue({
      tier: user.subscription?.tier || "free",
      expires_at: user.subscription?.expires_at
        ? dayjs(user.subscription.expires_at)
        : null,
    });
    setEditModalVisible(true);
  };

  // 更新用户订阅
  const handleUpdateSubscription = async (values: any) => {
    if (!editingUser) return;

    const selectedTier = tiers.find((t) => t.name === values.tier);
    if (!selectedTier) {
      message.error("未找到对应的会员等级");
      return;
    }

    try {
      const { error } = await supabaseClient
        .from("user_memberships")
        .upsert(
          {
            user_id: editingUser.id,
            tier_id: selectedTier.id,
            status: "active",
            started_at:
              editingUser.subscription?.status === "active" && editingUser.subscription.started_at
                ? editingUser.subscription.started_at
                : new Date().toISOString(),
            expires_at: values.expires_at ? dayjs(values.expires_at).toISOString() : null,
            updated_at: new Date().toISOString(),
          },
          { onConflict: "user_id" }
        );

      if (error) {
        message.error(`更新失败: ${error.message}`);
        return;
      }

      message.success("订阅更新成功");
      setEditModalVisible(false);
      form.resetFields();
      loadUsers();
      loadStats();
    } catch (err: any) {
      message.error(`更新异常: ${err.message}`);
    }
  };

  // 渲染订阅状态
  const renderSubscription = (subscription: any) => {
    if (!subscription) {
      return <Tag color="default">未订阅</Tag>;
    }

    const tierKey = subscription.tier || "unknown";
    const tierMeta = TIER_META[tierKey] || {
      label: subscription.displayName || tierKey,
      color: "default",
    };
    const tierLabel = subscription.displayName || tierMeta.label;
    const isExpired =
      subscription.expires_at &&
      new Date(subscription.expires_at) < new Date();
    const statusMeta = STATUS_META[subscription.status] || { label: subscription.status, color: "default" };

    return (
      <Space direction="vertical" size={4}>
        <Tag color={tierMeta.color} icon={tierKey === "vip" ? <CrownOutlined /> : null}>
          {tierLabel}
        </Tag>
        <Tag color={statusMeta.color}>
          {isExpired ? "已过期" : statusMeta.label}
        </Tag>
        {subscription.expires_at && (
          <Text
            type={isExpired ? "danger" : "secondary"}
            style={{ fontSize: 12 }}
          >
            {isExpired ? "已过期" : "有效期至"}{" "}
            {dayjs(subscription.expires_at).format("YYYY-MM-DD")}
            {!isExpired && `（${dayjs(subscription.expires_at).fromNow()}）`}
          </Text>
        )}
        {!subscription.expires_at && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            永久有效
          </Text>
        )}
      </Space>
    );
  };

  return (
    <>
      {/* 统计卡片 */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col xs={24} sm={12} md={6}>
            <Statistic
              title="总用户数"
              value={stats.totalUsers}
              prefix={<UserOutlined />}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Statistic
              title="付费用户"
              value={stats.paidUsers}
              valueStyle={{ color: "#3f8600" }}
              prefix={<CrownOutlined />}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Statistic
              title="免费用户"
              value={stats.freeUsers}
              valueStyle={{ color: "#999" }}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Statistic
              title="活跃订阅"
              value={stats.activeSubscriptions}
              valueStyle={{ color: "#1890ff" }}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Statistic
              title="7天内到期"
              value={stats.expiringSoon}
              valueStyle={{ color: "#faad14" }}
              prefix={<ClockCircleOutlined />}
            />
          </Col>
        </Row>
      </Card>

      <List
        title="用户管理"
        headerButtons={() => (
          <Space>
            <Input
              placeholder="搜索用户邮箱"
              prefix={<SearchOutlined />}
              value={searchEmail}
              onChange={(e) => setSearchEmail(e.target.value)}
              style={{ width: 200 }}
              allowClear
              onPressEnter={() => loadUsers()}
            />
            <Segmented
              options={[
                { label: "全部", value: "all" },
                { label: "活跃", value: "active" },
                { label: "过期", value: "expired" },
                { label: "未激活", value: "inactive" },
              ]}
              value={statusFilter}
              onChange={(val) => {
                setStatusFilter(val as any);
                setCurrentPage(1);
              }}
            />
            <Button type="primary" onClick={loadUsers}>
              搜索/筛选
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                loadUsers();
                loadStats();
              }}
            >
              刷新
            </Button>
          </Space>
        )}
      >
        <Table
          dataSource={users}
          rowKey="id"
          loading={loading}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 个用户`,
            onChange: (page, size) => {
              setCurrentPage(page);
              setPageSize(size || 20);
            },
          }}
        >
          <Table.Column
            dataIndex="email"
            title="用户邮箱"
            render={(email: string) => (
              <Space>
                <UserOutlined />
                <Text>{email}</Text>
              </Space>
            )}
          />
          <Table.Column
            dataIndex="subscription"
            title="订阅状态"
            render={(subscription) => renderSubscription(subscription)}
          />
          <Table.Column
            dataIndex="created_at"
            title="注册时间"
            render={(value: string) => dayjs(value).format("YYYY-MM-DD HH:mm")}
          />
          <Table.Column
            title="操作"
            dataIndex="actions"
            render={(_, record: UserWithSubscription) => (
              <Space>
                <Tooltip title="编辑订阅">
                  <Button
                    type="primary"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => openEditModal(record)}
                  >
                    编辑订阅
                  </Button>
                </Tooltip>
              </Space>
            )}
          />
        </Table>
      </List>

      {/* 编辑订阅对话框 */}
      <Modal
        title="编辑用户订阅"
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        okText="保存"
        cancelText="取消"
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleUpdateSubscription}
        >
          <Form.Item label="用户邮箱">
            <Input value={editingUser?.email} disabled />
          </Form.Item>

          <Form.Item
            label="订阅等级"
            name="tier"
            rules={[{ required: true, message: "请选择订阅等级" }]}
          >
            <Select
              placeholder="选择订阅等级"
              options={tiers.map((tier) => ({
                label: `${tier.display_name} (${tier.name})`,
                value: tier.name,
              }))}
            />
          </Form.Item>

          <Form.Item
            label="过期时间"
            name="expires_at"
            tooltip="留空表示永久有效"
          >
            <DatePicker
              style={{ width: "100%" }}
              showTime
              format="YYYY-MM-DD HH:mm:ss"
              placeholder="选择过期时间（可选）"
            />
          </Form.Item>

          <Form.Item>
            <Space direction="vertical" style={{ width: "100%" }}>
              <Text type="secondary">
                💡 提示：手动修改用户订阅后，系统会自动将状态设为"active"
              </Text>
              <Text type="warning">
                ⚠️ 注意：修改后会立即生效，请谨慎操作
              </Text>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};
